import { Fragment, useEffect, useRef, useState } from 'react';
import type { MeResponse, PriceBasis, PurchaseCard, PurchaseInput, PurchaseListItem, PurchaseOptions, PurchasePreview, PurchaseStatus, VatMode, VatSourceKind } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate, formatMoney, formatQty, hasPermission, UNIT_LABEL } from '../format';
import { useConfirm } from '../components/ConfirmDialog';
import { DateField } from '../components/DateField';
import { VatCheckBadge } from '../components/VatCheckBadge';
import { AckDialog, type AckTarget } from './VatCheck';
import { PendingSuppliers, PurchaseSource } from './PurchaseSource';
import { NotesPanel } from '../components/NotesPanel';

interface Props {
  me: MeResponse;
  onBack: () => void;
}

const STATUS_LABEL: Record<PurchaseStatus, string> = {
  draft: 'Qaralama',
  posted: 'Keçirilib',
  cancelled: 'Ləğv olunub',
};

export const VAT_LABEL: Record<VatMode, string> = { vat18: '18%', vat0: '0%', exempt: 'Azad', agri: 'K/t' };
const BASIS_LABEL: Record<PriceBasis, string> = { gross: 'ƏDV daxil', net: 'ƏDV üstünə' };
const SOURCE_LABEL: Record<VatSourceKind, string> = {
  e_invoice: 'E-qaimə',
  customs: 'Gömrük bəyannaməsi',
  e_purchase_act: 'Elektron alış aktı',
  paper_invoice: 'Kağız qaimə',
  receipt: 'Çek',
};

// Alış: siyahı və sənəd. Qaralama dəyişdirilir, keçirilir; keçirilmiş sənəd ləğv olunur (əks sənəd).
export function Purchases({ me, onBack }: Props) {
  // rev: hər saxlama/keçirmədən sonra forma yenidən qurulur ki, kart (status, maya) serverdən təzə oxunsun
  const [editing, setEditing] = useState<{ id: string | null; rev: number } | null>(null);
  const [fromDoc, setFromDoc] = useState(false);
  if (fromDoc && !editing) {
    return (
      <PurchaseSource
        me={me}
        onBack={() => setFromDoc(false)}
        onDraft={(id) => {
          setFromDoc(false);
          setEditing({ id, rev: 0 });
        }}
      />
    );
  }
  if (editing) {
    return (
      <PurchaseForm
        key={`${editing.id ?? 'new'}-${editing.rev}`}
        me={me}
        id={editing.id}
        onBack={() => setEditing(null)}
        onSaved={(id) => setEditing((cur) => ({ id, rev: (cur?.rev ?? 0) + 1 }))}
      />
    );
  }
  return <PurchaseList me={me} onBack={onBack} onOpen={(id) => setEditing({ id, rev: 0 })} onNew={() => setEditing({ id: null, rev: 0 })} onFromDoc={() => setFromDoc(true)} />;
}

function PurchaseList({ me, onBack, onOpen, onNew, onFromDoc }: { me: MeResponse; onBack: () => void; onOpen: (id: string) => void; onNew: () => void; onFromDoc: () => void }) {
  const [rev, setRev] = useState(0);
  const [status, setStatus] = useState('');
  const [items, setItems] = useState<PurchaseListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .purchases({ status: status || undefined, limit: 100 })
      .then((r) => !cancelled && setItems(r.items))
      .catch((err: unknown) => !cancelled && setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [status, rev]);

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn back-home" onClick={onBack}>
          ← Ana səhifə
        </button>
        <span className="brand">Alış</span>
        <span className="topbar-spacer" />
        {hasPermission(me, 'purchase.create') && (
          <>
            <button type="button" className="btn-small" onClick={onFromDoc}>
              Sənəddən
            </button>
            <button type="button" className="btn-small" onClick={onNew}>
              + Yeni alış
            </button>
          </>
        )}
      </header>
      <main className="page">
        {hasPermission(me, 'approve.accounting') && <PendingSuppliers onChanged={() => setRev((r) => r + 1)} />}
        <div className="toolbar">
          <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Hamısı</option>
            <option value="draft">Qaralama</option>
            <option value="posted">Keçirilib</option>
            <option value="cancelled">Ləğv olunub</option>
          </select>
        </div>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {loading && <p className="muted">Yüklənir…</p>}
        {!loading && !error && items.length === 0 && <p className="muted">Alış sənədi yoxdur.</p>}
        {items.length > 0 && (
          <ul className="product-list">
            {items.map((p) => (
              <li key={p.id}>
                <button type="button" className="product-row" onClick={() => onOpen(p.id)}>
                  <span className="product-name">
                    <span className="mono small">{p.displayNo}</span> {p.supplierName}
                  </span>
                  <span className="muted small">
                    {formatDate(p.docDate)} · {p.warehouseName} · {STATUS_LABEL[p.status]}
                  </span>
                  <span className="product-meta">
                    <span className="mono">{formatMoney(p.total)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

const NOTICE_KEY = 'kassa.purchase.notice';

interface FormLine {
  productId: string;
  name: string;
  unit: string;
  /** Satış qiyməti (yeni): boş — yenilənmir; oldSale — indiki (göstərmək üçün) */
  salePrice: string;
  oldSale: number | null;
  qty: string;
  unitPrice: string;
  discount: string;
  /** '' — «ƏDV seçilməyib» (KT-66): sistem 18 qoymur. */
  vatMode: VatMode | '';
  /** '' — sənədin qiymət əsası. */
  priceBasis: PriceBasis | '';
  /** Malın satış növü (2.3 «Alışdan satışa» üçün). */
  productVatMode: VatMode | null;
  saleModeAction: 'switch' | 'keep' | '';
  saleModeReason: string;
}

function PurchaseForm({ me, id, onBack, onSaved }: { me: MeResponse; id: string | null; onBack: () => void; onSaved: (id: string) => void }) {
  const confirm = useConfirm();
  const [options, setOptions] = useState<PurchaseOptions | null>(null);
  const [card, setCard] = useState<PurchaseCard | null>(null);
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [docDate, setDocDate] = useState('');
  const [note, setNote] = useState('');
  const [priceBasis, setPriceBasis] = useState<PriceBasis | ''>('');
  const [sourceKind, setSourceKind] = useState<VatSourceKind | ''>('');
  const [sourceNo, setSourceNo] = useState('');
  const [sourceDate, setSourceDate] = useState('');
  const [notice, setNotice] = useState<string[]>(() => {
    try {
      const raw = window.sessionStorage.getItem(NOTICE_KEY);
      window.sessionStorage.removeItem(NOTICE_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [lines, setLines] = useState<FormLine[]>([]);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<{ id: string; name: string; unitCode: string; vatMode: VatMode | null; retailPrice: number | null }[]>([]);
  // «Satış qiymətini də yenilə» (KT-108): susmaya görə açıq (dizayn), yalnız price.edit olanda
  const [updateSale, setUpdateSale] = useState(true);
  const canSalePrice = hasPermission(me, 'price.edit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readOnly = card !== null && card.status !== 'draft';
  const canEdit = hasPermission(me, id ? 'purchase.edit' : 'purchase.create') && !readOnly;

  useEffect(() => {
    api.purchaseOptions().then(setOptions).catch(() => setOptions({ suppliers: [], warehouses: [], moneyAccounts: [], vatPayer: false }));
    if (id) {
      api
        .purchaseCard(id)
        .then((c) => {
          setCard(c);
          setSupplierId(c.supplierId);
          setWarehouseId(c.warehouseId);
          setDocDate(c.docDate.slice(0, 10));
          setNote(c.note ?? '');
          setPriceBasis(c.priceBasis ?? '');
          setSourceKind(c.sourceKind ?? '');
          setSourceNo(c.sourceNo ?? '');
          setSourceDate(c.sourceDate ?? '');
          setLines(
            c.lines.map((l) => ({
              productId: l.productId,
              name: l.productName,
              unit: l.unit,
              salePrice: '',
              oldSale: null,
              qty: String(l.qty),
              unitPrice: String(l.unitPrice),
              discount: String(l.discount),
              vatMode: l.vatMode ?? '',
              priceBasis: l.priceBasis ?? '',
              productVatMode: l.productVatMode,
              saleModeAction: l.saleModeAction ?? '',
              saleModeReason: l.saleModeReason ?? '',
            })),
          );
        })
        .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Sənəd yüklənmədi'));
    }
  }, [id]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setFound([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .products({ q: search.trim(), limit: 10 })
        .then((r) => setFound(r.items.map((p) => ({ id: p.id, name: p.name, unitCode: p.unitCode, vatMode: p.vatMode ?? null, retailPrice: p.retailPrice }))))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  function addProduct(p: { id: string; name: string; unitCode: string; vatMode: VatMode | null; retailPrice?: number | null }) {
    setLines((cur) => [...cur, { productId: p.id, name: p.name, unit: p.unitCode, salePrice: '', oldSale: p.retailPrice ?? null, qty: '1', unitPrice: '0', discount: '0', vatMode: '', priceBasis: '', productVatMode: p.vatMode, saleModeAction: '', saleModeReason: '' }]);
    setSearch('');
    setFound([]);
  }

  // Son sətir: ad və ya barkod + Enter — dəqiq barkod/kod tapılarsa dərhal əlavə, yoxsa siyahıdan seçilir
  const addByCode = async () => {
    const q = search.trim();
    if (q.length < 2) return;
    try {
      const r = await api.products({ q, limit: 5 });
      const hit = r.items.find((p) => p.barcodes.includes(q)) ?? (r.items.length === 1 ? r.items[0] : null);
      if (hit) addProduct({ id: hit.id, name: hit.name, unitCode: hit.unitCode, vatMode: hit.vatMode ?? null });
      else setFound(r.items.map((p) => ({ id: p.id, name: p.name, unitCode: p.unitCode, vatMode: p.vatMode ?? null, retailPrice: p.retailPrice })));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Axtarış alınmadı');
    }
  };

  function patchLine(i: number, field: keyof FormLine, value: string) {
    setLines((cur) => cur.map((l, k) => (k === i ? { ...l, [field]: value } : l)));
  }

  function applyAll(field: 'vatMode' | 'priceBasis', value: string) {
    if (!value) return;
    setLines((cur) => cur.map((l) => ({ ...l, [field]: value })));
  }

  const isUnset = (l: FormLine) => !l.vatMode || (!l.priceBasis && !priceBasis);
  const unsetCount = lines.filter(isUnset).length;
  // «Alışdan satışa» (2.3): 18% alış, malın satış növü azad/0% — 18%-ə keç və ya səbəblə saxla
  const isConflict = (l: FormLine) => l.vatMode === 'vat18' && (l.productVatMode === 'exempt' || l.productVatMode === 'vat0');
  const isUnresolved = (l: FormLine) => isConflict(l) && (!l.saleModeAction || (l.saleModeAction === 'keep' && !l.saleModeReason.trim()));
  const conflictCount = lines.filter(isUnresolved).length;

  // Cəmilər serverdən (KT-108): sətir üzrə ƏDV, cəm, yekun — KT-66 qaydası ilə, klientdə float hesab yoxdur
  const [totals, setTotals] = useState<PurchasePreview | null>(null);
  // XİF MN üzrə ƏDV yoxlaması (KT-69, 2.9): sətir statusu preview-dən; «Seçimim düzdür»dən sonra yenilənir
  const [acking, setAcking] = useState<AckTarget | null>(null);
  const [vatSeq, setVatSeq] = useState(0);
  // Sətir statusu yalnız cari sətirlərin preview-dən (sətir əlavə/silinəndən sonra köhnə cavab başqa sətrə düşməsin)
  const [totalsKey, setTotalsKey] = useState('');
  const vatLine = (i: number) => (totalsKey === previewKey ? totals?.lines[i]?.vatCheck ?? null : null);
  const vatBlocked = (i: number) => {
    const lv = vatLine(i)?.level;
    return lv === 'red' || lv === 'yellow';
  };
  const linesValid = lines.length > 0 && lines.every((l) => Number(l.qty) > 0 && Number(l.unitPrice) >= 0 && Number(l.discount || 0) >= 0);
  const previewKey = JSON.stringify([docDate, priceBasis, sourceKind, lines.map((l) => [l.productId, l.qty, l.unitPrice, l.discount, l.vatMode, l.priceBasis])]);
  const vatBad = lines.filter((_, i) => vatBlocked(i)).length;
  useEffect(() => {
    // Köhnə cəm (məs. sətir silinəndən sonra) yeni cavab gələnə qədər görünməsin; redaktə hüququ yoxdursa — kartdakı cəm
    setTotals(null);
    if (readOnly || !canEdit || !linesValid) return;
    let live = true;
    const key = previewKey;
    const t = window.setTimeout(() => {
      const b = body();
      api
        .purchasePreview({ docDate: b.docDate, priceBasis: b.priceBasis, sourceKind: b.sourceKind, lines: b.lines })
        .then((r) => {
          if (!live) return;
          setTotals(r);
          setTotalsKey(key);
        })
        .catch(() => live && setTotals(null)); // yarımçıq sətir (məs. endirim > məbləğ) — cəm göstərilmir, saxlama xətanı deyəcək
    }, 300);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [previewKey, readOnly, canEdit, vatSeq]);

  function body(): PurchaseInput {
    return {
      supplierId,
      warehouseId,
      docDate: docDate || null,
      note: note.trim() || null,
      priceBasis: priceBasis || null,
      sourceKind: sourceKind || null,
      sourceNo: sourceNo.trim() || null,
      sourceDate: sourceDate || null,
      lines: lines.map((l) => ({
        productId: l.productId,
        qty: Number(l.qty),
        unitPrice: Number(l.unitPrice),
        discount: Number(l.discount || 0),
        vatMode: l.vatMode || null,
        priceBasis: l.priceBasis || null,
        saleModeAction: isConflict(l) && l.saleModeAction && (l.saleModeAction === 'switch' || l.saleModeReason.trim()) ? l.saleModeAction : null,
        saleModeReason: isConflict(l) && l.saleModeAction === 'keep' ? l.saleModeReason.trim() || null : null,
      })),
    };
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı. Yenidən cəhd edin.');
      setVatSeq((v) => v + 1); // 409 vat_check və s. — sətir statusları yenidən soruşulsun
    } finally {
      setBusy(false);
    }
  }

  // Yaranmış qaralamanın id-si: təsdiq alınmasa təkrar basış yeni sənəd yaratmasın (həmin qaralama yenilənir)
  const createdId = useRef<string | null>(null);
  const save = () =>
    run(async () => {
      const known = id ?? createdId.current;
      const r = known ? await api.purchaseUpdate(known, body()).then(() => ({ id: known })) : await api.purchaseCreate(body());
      onSaved(r.id);
    });

  // «Saxla və təsdiqlə» bir addımda: yeni sənəd əvvəl qaralama kimi yaranır, sonra keçirilir
  // «İndi ödənilir» (KT-108 b-3): təsdiqlə eyni anda ödəniş — bir tranzaksiya, təkrar göndəriş eyni ödənişi tanıyır
  const [payNow, setPayNow] = useState(false);
  const [payMethod, setPayMethod] = useState<'cash' | 'transfer'>('transfer');
  const [payAccount, setPayAccount] = useState('');
  const [payAmount, setPayAmount] = useState('');
  // ƏDV depoziti: köçürmədə ƏDV-nin əvəzləşdirilən hissəsi ayrıca (boş — 0; nağdda depozit olmur)
  const [payDeposit, setPayDeposit] = useState('');
  const payId = useRef<string>(crypto.randomUUID());
  const payAmountNum = Number(payAmount.replace(',', '.').trim());
  const payDepositNum = payMethod === 'transfer' && payDeposit.trim() !== '' ? Number(payDeposit.replace(',', '.').trim()) : 0;
  const payload = () =>
    payNow ? { id: payId.current, method: payMethod, moneyAccountId: payAccount, amount: payAmountNum, vatDeposit: payDepositNum } : undefined;
  // Satış qiyməti yazılmış sətirlər (boşlar yenilənmir); yalnız updateSale açıqdırsa
  const salePricesPayload = () =>
    updateSale && canSalePrice ? lines.filter((l) => l.salePrice.trim() !== '').map((l) => ({ productId: l.productId, price: Number(l.salePrice.replace(',', '.')) })) : [];
  const badSalePrice = updateSale && canSalePrice && lines.some((l) => l.salePrice.trim() !== '' && !(Number(l.salePrice.replace(',', '.')) >= 0));
  const post = () => {
    if (badSalePrice) {
      setError('Satış qiyməti düzgün deyil (0 və ya müsbət ədəd)');
      return;
    }
    // «İndi ödənilir» açıqdırsa — hesab və məbləğ düzgün olmalıdır; səssiz ödənişsiz keçirmək olmaz
    if (payNow && (!payAccount || !(payAmountNum > 0))) {
      setError('«İndi ödənilir» üçün hesab seçin və düzgün məbləğ yazın');
      return;
    }
    return run(async () => {
      const known = id ?? createdId.current;
      const docId = known ? (await api.purchaseUpdate(known, body()), known) : (createdId.current = (await api.purchaseCreate(body())).id);
      // Xəbərdarlıqlar kartda görünür (forma yenidən yüklənir)
      const posted = await api.purchasePost(docId, payload(), salePricesPayload());
      // Xəbərdarlıqlar (ƏDV, obyekt qiyməti) forma yenidən açılanda da görünsün
      const w = [...posted.warnings, ...(posted.payment?.warnings ?? [])];
      try {
        if (w.length) window.sessionStorage.setItem(NOTICE_KEY, JSON.stringify(w));
      } catch {
        /* yaddaş yoxdur — xəbərdarlıq bu dəfə görünməyə bilər */
      }
      onSaved(docId);
    });
  };

  // Ctrl+S — qaralama, Ctrl+Enter — saxla və təsdiqlə
  const keys = useRef({ save: () => undefined as unknown, post: () => undefined as unknown, can: false, canPost: false });
  keys.current = { save, post, can: canEdit && !busy && Boolean(supplierId && warehouseId && lines.length), canPost: hasPermission(me, 'purchase.post') && unsetCount === 0 && conflictCount === 0 && vatBad === 0 };
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !(e.ctrlKey || e.metaKey) || document.querySelector('[aria-modal="true"]')) return;
      if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (keys.current.can) void keys.current.save();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (keys.current.can && keys.current.canPost) void keys.current.post();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const changeStatus = async (lineId: string, status: 'evez' | 'maya') => {
    if (!id) return;
    const text = status === 'maya' ? 'Bu sətrin ƏDV-si mayaya köçürülsün? Maya düzəliş sənədi yazılacaq.' : 'Bu sətrin ƏDV-si əvəzləşdirilsin? Maya ƏDV qədər azalacaq.';
    if (!(await confirm(text, { confirmLabel: 'Təsdiqlə' }))) return;
    run(async () => {
      await api.purchaseOffsetStatus(id, lineId, status);
      onSaved(id);
    });
  };

  const pay = (body: { method: 'cash' | 'transfer'; moneyAccountId: string; amount: number; vatDeposit: number }) =>
    run(async () => {
      if (!id) return;
      const r = await api.purchasePay(id, { id: crypto.randomUUID(), ...body });
      setNotice(r.warnings.length > 0 ? r.warnings : ['Ödəniş yazıldı.']);
      setCard(await api.purchaseCard(id));
    });

  const cancel = async () => {
    if (!id || !(await confirm('Bu alış ləğv olunsun? Əks hərəkətlər yazılacaq, sənəd silinmir.', { confirmLabel: 'Ləğv et', danger: true }))) return;
    run(async () => {
      await api.purchaseCancel(id);
      onSaved(id);
    });
  };

  const t = (i: number) => totals?.lines[i];
  return (
    <main className="doc-form sec-anbar">
      <header className="doc-head">
        <div>
          <span className="doc-eyebrow">MAL VƏ ANBAR · ALIŞ</span>
          <h1>
            {card ? `Alış qaiməsi ${card.displayNo}` : 'Yeni alış qaiməsi'}{' '}
            <span className={`badge ${!card || card.status === 'draft' ? 'badge-off' : card.status === 'posted' ? 'badge-ok' : 'badge-bad'}`}>{card ? STATUS_LABEL[card.status] : 'Qaralama'}</span>
          </h1>
          <span className="muted small">{card ? `${card.displayNo} · ${formatDate(card.docDate)}` : 'Nömrə saxlananda verilir'}</span>
        </div>
        <button type="button" className="btn-secondary" onClick={onBack}>
          Siyahıya qayıt
        </button>
      </header>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}

      <section className="card doc-section" aria-label="Sənəd məlumatları">
        <h2>Sənəd məlumatları</h2>
        <div className="doc-grid">
          <label className="field">
            <span>Nömrə</span>
            <input value={card?.displayNo ?? 'avtomatik'} readOnly disabled className="doc-readonly" />
          </label>
          <label className="field">
            <span>Tarix *</span>
            <DateField value={docDate} disabled={!canEdit} onChange={setDocDate} />
          </label>
          <label className="field">
            <span>Anbar *</span>
            <select aria-label="Anbar *" value={warehouseId} disabled={!canEdit} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">Seçin</option>
              {options?.warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.outletName} · {w.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Təchizatçı *</span>
            <select aria-label="Təchizatçı *" value={supplierId} disabled={!canEdit} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Seçin</option>
              {options?.suppliers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
            {card?.supplierVoen && <span className="doc-hint">VÖEN {card.supplierVoen}</span>}
          </label>
          <label className="field">
            <span>Təchizatçının qaimə nömrəsi</span>
            <input type="text" value={sourceNo} disabled={!canEdit} onChange={(e) => setSourceNo(e.target.value)} maxLength={60} />
          </label>
          <label className="field">
            <span>Mənbə (ƏDV əvəzləşdirməsi üçün)</span>
            <select value={sourceKind} disabled={!canEdit} onChange={(e) => setSourceKind(e.target.value as VatSourceKind | '')}>
              <option value="">Göstərilməyib</option>
              {(Object.keys(SOURCE_LABEL) as VatSourceKind[]).map((k) => (
                <option key={k} value={k}>
                  {SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Mənbənin tarixi</span>
            <DateField value={sourceDate} disabled={!canEdit} onChange={setSourceDate} />
          </label>
          <label className="field">
            <span>Qiymət əsası (sənəd üzrə)</span>
            <select value={priceBasis} disabled={!canEdit} onChange={(e) => setPriceBasis(e.target.value as PriceBasis | '')}>
              <option value="">Seçilməyib</option>
              <option value="gross">{BASIS_LABEL.gross}</option>
              <option value="net">{BASIS_LABEL.net}</option>
            </select>
          </label>
          <label className="field doc-wide">
            <span>Qeyd</span>
            <input type="text" value={note} disabled={!canEdit} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Əlavə məlumat…" />
          </label>
        </div>
      </section>

      {(unsetCount > 0 || conflictCount > 0 || vatBad > 0 || (card?.warnings.length ?? 0) > 0) && (
        <section className="card vat-notice" role="status">
          {unsetCount > 0 && <p><strong>{unsetCount} sətirdə ƏDV seçilməyib — sənəd keçirilmir.</strong> Hər sətirdə ƏDV növünü və qiymət əsasını göstərin.</p>}
          {conflictCount > 0 && (
            <p className="vat-over">
              {conflictCount} mal ƏDV ilə alınıb, satışda isə ƏDV-siz qeyd olunub — həll olunmayınca sənəd keçirilmir.
            </p>
          )}
          {vatBad > 0 && (
            <p className="vat-over">
              ƏDV yoxlaması: {lines.length} maldan {vatBad}-{vatBad === 1 ? 'i' : 'si'} XİF MN koduna uyğun gəlmir. Düzəldin və ya seçiminizi təsdiqləyin — həll olunmayınca sənəd keçirilmir.
            </p>
          )}
          {card?.warnings
            .filter((w) => !w.includes('seçilməyib') && !w.includes('ƏDV ilə alınıb'))
            .map((w) => (
              <p key={w} className="small">
                {w}
              </p>
            ))}
        </section>
      )}

      {!readOnly && (
        <section className="card doc-section" aria-label="Mallar">
          <div className="doc-section-head">
            <h2>Mallar · {lines.length} sətir</h2>
            {canEdit && canSalePrice && (
              <label className="inline-check">
                <input type="checkbox" checked={updateSale} onChange={(e) => setUpdateSale(e.target.checked)} /> Satış qiymətini də yenilə
              </label>
            )}
            {canEdit && lines.length > 1 && (
              <div className="toolbar">
                <span className="muted small">Bütün sətirlərə:</span>
                <select aria-label="Bütün sətirlərə ƏDV növü" value="" onChange={(e) => applyAll('vatMode', e.target.value)}>
                  <option value="">ƏDV növü…</option>
                  {(Object.keys(VAT_LABEL) as VatMode[]).map((m) => (
                    <option key={m} value={m}>
                      {VAT_LABEL[m]}
                    </option>
                  ))}
                </select>
                <select aria-label="Bütün sətirlərə qiymət əsası" value="" onChange={(e) => applyAll('priceBasis', e.target.value)}>
                  <option value="">Qiymət əsası…</option>
                  <option value="gross">{BASIS_LABEL.gross}</option>
                  <option value="net">{BASIS_LABEL.net}</option>
                </select>
              </div>
            )}
          </div>
          <div className="table-wrap">
            <table className="data-table doc-lines" aria-label="Mallar">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Mal</th>
                  <th scope="col" className="num">
                    Miqdar
                  </th>
                  <th scope="col" className="num">
                    Qiymət
                  </th>
                  <th scope="col">ƏDV</th>
                  <th scope="col">Qiymət əsası</th>
                  <th scope="col" className="num">
                    Endirim
                  </th>
                  {updateSale && canSalePrice && (
                    <th scope="col" className="num">
                      Satış qiyməti
                    </th>
                  )}
                  <th scope="col" className="num">
                    ƏDV məbləği
                  </th>
                  <th scope="col" className="num">
                    Cəmi
                  </th>
                  <th scope="col">
                    <span className="sr-only">Sil</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <Fragment key={`${l.productId}-${i}`}>
                    <tr className={`${isUnset(l) ? 'vat-unset' : ''}${isUnresolved(l) ? ' vat-conflict' : ''}`}>
                      <td className="muted">{i + 1}</td>
                      <td>
                        <span className="product-name">{l.name}</span>
                        {l.vatMode === 'vat18' && l.productVatMode === null && <span className="muted small"> · alışa görə: 18%</span>}
                      </td>
                      <td className="num">
                        <span className="doc-qty">
                          <input type="number" min="0" step="0.001" aria-label={`${l.name} miqdar`} value={l.qty} disabled={!canEdit} onChange={(e) => patchLine(i, 'qty', e.target.value)} />
                          <span className="muted small">{UNIT_LABEL[l.unit] ?? l.unit}</span>
                        </span>
                      </td>
                      <td className="num">
                        <input type="number" min="0" step="0.0001" aria-label={`${l.name} qiymət`} value={l.unitPrice} disabled={!canEdit} onChange={(e) => patchLine(i, 'unitPrice', e.target.value)} />
                      </td>
                      <td>
                        <select aria-label={`${l.name} ƏDV növü`} value={l.vatMode} disabled={!canEdit} onChange={(e) => patchLine(i, 'vatMode', e.target.value)}>
                          <option value="">Seçilməyib</option>
                          {(Object.keys(VAT_LABEL) as VatMode[]).map((m) => (
                            <option key={m} value={m}>
                              {VAT_LABEL[m]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select aria-label={`${l.name} qiymət əsası`} value={l.priceBasis} disabled={!canEdit} onChange={(e) => patchLine(i, 'priceBasis', e.target.value)}>
                          <option value="">{priceBasis ? `Sənəd üzrə (${BASIS_LABEL[priceBasis]})` : 'Seçilməyib'}</option>
                          <option value="gross">{BASIS_LABEL.gross}</option>
                          <option value="net">{BASIS_LABEL.net}</option>
                        </select>
                      </td>
                      <td className="num">
                        <input type="number" min="0" step="0.01" aria-label={`${l.name} endirim`} value={l.discount} disabled={!canEdit} onChange={(e) => patchLine(i, 'discount', e.target.value)} />
                      </td>
                      {updateSale && canSalePrice && (
                        <td className="num">
                          <input type="number" min="0" step="0.0001" aria-label={`${l.name} satış qiyməti`} placeholder={l.oldSale !== null ? String(l.oldSale) : '—'} value={l.salePrice} disabled={!canEdit} onChange={(e) => patchLine(i, 'salePrice', e.target.value)} />
                          {l.oldSale !== null && <span className="doc-hint">köhnə: {formatMoney(l.oldSale)}</span>}
                        </td>
                      )}
                      <td className="num mono">{t(i) ? (t(i)!.vatSet ? formatMoney(t(i)!.vatAmount) : '—') : '…'}</td>
                      <td className="num mono">{t(i) ? formatMoney(t(i)!.amount) : '…'}</td>
                      <td>
                        {canEdit && (
                          <button type="button" className="doc-del" aria-label={`${l.name} sətrini sil`} onClick={() => setLines((cur) => cur.filter((_, k) => k !== i))}>
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                    {isConflict(l) && (
                      <tr className="vat-conflict">
                        <td />
                        <td colSpan={10}>
                          <div className="sale-mode">
                            <p className="small vat-over">
                              Bu mal ƏDV ilə alınıb, satışda isə ƏDV-siz ({l.productVatMode ? VAT_LABEL[l.productVatMode] : ''}) qeyd olunub.
                            </p>
                            {canEdit ? (
                              <div className="toolbar">
                                <button type="button" className={l.saleModeAction === 'switch' ? 'btn-small is-active' : 'btn-small'} onClick={() => patchLine(i, 'saleModeAction', 'switch')}>
                                  18%-ə keçir
                                </button>
                                <button type="button" className={l.saleModeAction === 'keep' ? 'btn-small is-active' : 'btn-small'} onClick={() => patchLine(i, 'saleModeAction', 'keep')}>
                                  Səbəblə saxla
                                </button>
                                {l.saleModeAction === 'keep' && (
                                  <input type="text" aria-label="Saxlama səbəbi" placeholder="Səbəb (məs. ixrac — 0%)" maxLength={300} value={l.saleModeReason} onChange={(e) => patchLine(i, 'saleModeReason', e.target.value)} />
                                )}
                              </div>
                            ) : (
                              l.saleModeAction && <p className="muted small">{l.saleModeAction === 'switch' ? '18%-ə keçirilir' : `Saxlanılır: ${l.saleModeReason}`}</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {(() => {
                      const vc = vatLine(i);
                      if (!vc || (vc.level !== 'red' && vc.level !== 'yellow' && vc.level !== 'blue' && !vc.expiry)) return null;
                      return (
                        <tr className="vat-conflict">
                          <td />
                          <td colSpan={10}>
                            <div className="sale-mode">
                              <p className="small">
                                {vc.level !== 'green' && <VatCheckBadge level={vc.level} />} {vc.level === 'red' || vc.level === 'yellow' ? vc.title : vc.level === 'blue' ? 'Seçim təsdiqlənib.' : ''}
                                {vc.expiry && <span className="muted vat-check-note">{vc.expiry}</span>}
                              </p>
                              {canEdit && vc.level === 'red' && (
                                <div className="toolbar">
                                  {vc.suggested && (
                                    <button type="button" className="btn-small" onClick={() => patchLine(i, 'vatMode', vc.suggested!)}>
                                      {VAT_LABEL[vc.suggested]} et
                                    </button>
                                  )}
                                  {l.vatMode ? (
                                    <button type="button" className="btn-small" onClick={() => setAcking({ productId: l.productId, name: l.name, chosen: l.vatMode as VatMode, suggested: vc.suggested, line: true })}>
                                      Seçimim düzdür
                                    </button>
                                  ) : (
                                    <span className="muted small">Təsdiq üçün əvvəl sətirdə ƏDV növünü seçin</span>
                                  )}
                                </div>
                              )}
                              {vc.level === 'yellow' && <p className="muted small">Mal kartında XİF MN kodunu tam yazın (10 rəqəm).</p>}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </Fragment>
                ))}
                {canEdit && (
                  <tr className="doc-add">
                    <td className="muted">{lines.length + 1}</td>
                    <td colSpan={10}>
                      <input
                        type="search"
                        className="search"
                        placeholder="Mal əlavə et: ad və ya barkod yaz, Enter"
                        aria-label="Məhsul axtarışı"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                            e.preventDefault();
                            void addByCode();
                          }
                        }}
                      />
                      {found.length > 0 && (
                        <ul className="product-list">
                          {found.map((p) => (
                            <li key={p.id}>
                              <button type="button" className="product-row" onClick={() => addProduct(p)}>
                                <span className="product-name">{p.name}</span>
                                <span className="muted small">{UNIT_LABEL[p.unitCode] ?? p.unitCode}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {card && readOnly && (
        <section className="card doc-section" aria-label="Mallar">
          <h2>Mallar · {card.lines.length} sətir</h2>
          <ul className="product-list">
            {card.lines.map((l) => (
              <li key={l.id} className="muted small">
                {l.productName}: {formatQty(l.qty)} × {formatMoney(l.unitPrice)}
                {l.vatMode && ` · ${VAT_LABEL[l.vatMode]}`}
                {l.priceBasis && ` (${BASIS_LABEL[l.priceBasis]})`} · ƏDV {formatMoney(l.vatAmount)} · maya {formatMoney(l.costAmount)}
                {l.offsetStatus && (
                  <span className="tag" title={l.offsetStatus === 'evez' ? 'ƏDV əvəzləşdirilir, maya ƏDV-siz' : 'ƏDV mayaya düşür'}>
                    {l.offsetStatus === 'evez' ? 'əvəzləşdirilir' : 'mayaya düşür'}
                  </span>
                )}
                {card.status === 'posted' && l.offsetStatus && hasPermission(me, 'vat.manage') && (
                  <button type="button" className="link-btn" disabled={busy} onClick={() => changeStatus(l.id, l.offsetStatus === 'evez' ? 'maya' : 'evez')}>
                    {l.offsetStatus === 'evez' ? 'Mayaya köçür' : 'Əvəzləşdir'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card doc-section doc-pay" aria-label="Ödəniş">
        <div>
          <h2>Ödəniş</h2>
          {card?.status === 'posted' && hasPermission(me, 'approve.accounting') && options ? (
            <PaymentPanel card={card} options={options} busy={busy} onPay={pay} />
          ) : (
            <>
              <p className="doc-info">Təsdiqdən sonra qalıq artır, təchizatçıya borc yazılır. Sənəd silinmir — səhv olsa ləğv sənədi yaradılır.</p>
              {canEdit && hasPermission(me, 'approve.accounting') && options && !readOnly && (
                <div className="doc-paynow">
                  <label className="inline-check">
                    <input type="checkbox" checked={payNow} onChange={(e) => setPayNow(e.target.checked)} /> İndi ödənilir
                  </label>
                  {payNow && (
                    <div className="doc-grid">
                      <label className="field">
                        <span>Üsul</span>
                        <select value={payMethod} onChange={(e) => { setPayMethod(e.target.value as 'cash' | 'transfer'); setPayAccount(''); }}>
                          <option value="transfer">Köçürmə</option>
                          <option value="cash">Nağd</option>
                        </select>
                      </label>
                      <label className="field">
                        <span>Hesab</span>
                        <select value={payAccount} onChange={(e) => setPayAccount(e.target.value)}>
                          <option value="">Seçin</option>
                          {options.moneyAccounts.filter((a) => a.kind === (payMethod === 'cash' ? 'cash' : 'bank')).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="field">
                        <span>Məbləğ, ₼</span>
                        <input inputMode="decimal" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="məs. 1000,00" />
                      </label>
                      {payMethod === 'transfer' && (
                        <label className="field">
                          <span>ƏDV depoziti, ₼</span>
                          <input inputMode="decimal" value={payDeposit} onChange={(e) => setPayDeposit(e.target.value)} placeholder="əvəzləşdiriləcək ƏDV (boş — 0)" />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {notice.length > 0 && (
            <div className="vat-notice" role="status">
              {notice.map((w) => (
                <p key={w} className="small">
                  {w}
                </p>
              ))}
            </div>
          )}
        </div>
        <dl className="doc-totals" aria-label="Cəmilər">
          <dt>Mallar (ƏDV-siz)</dt>
          <dd className="mono">{card && (readOnly || !canEdit) ? formatMoney(card.subtotal) : totals ? formatMoney(totals.net) : '—'}</dd>
          <dt>ƏDV (sətir üzrə)</dt>
          <dd className="mono">{card && (readOnly || !canEdit) ? formatMoney(card.vatAmount) : totals ? formatMoney(totals.vat) : '—'}</dd>
          <dt className="doc-total-label">Yekun</dt>
          <dd className="mono doc-total">{card && (readOnly || !canEdit) ? formatMoney(card.total) : totals ? formatMoney(totals.total) : '—'}</dd>
        </dl>
      </section>

      {acking && (
        <AckDialog
          row={acking}
          onCancel={() => setAcking(null)}
          onDone={() => {
            setAcking(null);
            setVatSeq((v) => v + 1);
          }}
        />
      )}

      {/* Sənəd qeydləri (KT-76) — keçirilmiş və qaralama sənəddə */}
      {id && card && (
        <section className="card">
          <NotesPanel target={{ docId: id }} />
        </section>
      )}

      <footer className="doc-bar">
        <span className="muted small doc-bar-hint">{canEdit ? 'Ctrl+S — qaralama · Ctrl+Enter — təsdiqlə · Enter — mal əlavə et' : ''}</span>
        {card?.status === 'posted' && hasPermission(me, 'purchase.post') && (
          <button type="button" className="btn-secondary doc-cancel" onClick={cancel} disabled={busy}>
            Ləğv et
          </button>
        )}
        {canEdit && (
          <button type="button" className="btn-secondary" onClick={save} disabled={busy || !supplierId || !warehouseId || lines.length === 0}>
            Qaralama saxla
          </button>
        )}
        {canEdit && hasPermission(me, 'purchase.post') && (
          <button type="button" className="btn-primary" onClick={post} disabled={busy || !supplierId || !warehouseId || lines.length === 0 || unsetCount > 0 || conflictCount > 0 || vatBad > 0}>
            Saxla və təsdiqlə
          </button>
        )}
      </footer>
    </main>
  );
}

/** Təchizatçıya ödəniş (KT-66, 2.5): ƏDV hissəsi depozitə ayrıca; nağd ödənişdə ƏDV əvəzləşdirilmir. */
function PaymentPanel({ card, options, busy, onPay }: {
  card: PurchaseCard;
  options: PurchaseOptions;
  busy: boolean;
  onPay: (b: { method: 'cash' | 'transfer'; moneyAccountId: string; amount: number; vatDeposit: number }) => void;
}) {
  const [method, setMethod] = useState<'cash' | 'transfer'>('transfer');
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [deposit, setDeposit] = useState('');
  const remaining = Math.round((card.total - card.paid) * 100) / 100;
  const evezVat = card.lines.filter((l) => l.offsetStatus === 'evez').reduce((s, l) => s + l.vatAmount, 0);
  const accounts = options.moneyAccounts.filter((a) => a.kind === (method === 'cash' ? 'cash' : 'bank'));
  if (remaining <= 0) return <section className="card"><p className="muted small">Ödənilib: {formatMoney(card.paid)} (depozitə {formatMoney(card.vatDeposited)})</p></section>;
  return (
    <section className="card">
      <h2>Təchizatçıya ödəniş</h2>
      <p className="muted small">Qalan borc {formatMoney(remaining)} · ödənilib {formatMoney(card.paid)} · depozitə {formatMoney(card.vatDeposited)}</p>
      <div className="form-grid">
        <label>
          Üsul
          <select value={method} onChange={(e) => { setMethod(e.target.value as 'cash' | 'transfer'); setAccountId(''); }}>
            <option value="transfer">Köçürmə</option>
            <option value="cash">Nağd</option>
          </select>
        </label>
        <label>
          Hesab
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Seçin</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {method === 'transfer' ? 'Təchizatçının hesabına' : 'Məbləğ'}
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        {method === 'transfer' && (
          <label>
            ƏDV depozit hesabına
            <input type="number" min="0" step="0.01" value={deposit} onChange={(e) => setDeposit(e.target.value)} />
          </label>
        )}
      </div>
      {method === 'cash' && evezVat > 0 && (
        <p className="small vat-warn" role="status">Nağd ödənişdə ƏDV əvəzləşdirilməyəcək — mühasibə «mayaya köçür» tapşırığı yaranacaq.</p>
      )}
      <div className="toolbar">
        <button
          type="button"
          className="btn-small"
          disabled={busy || !accountId || Number(amount || 0) + Number(deposit || 0) <= 0}
          onClick={() => onPay({ method, moneyAccountId: accountId, amount: Number(amount || 0), vatDeposit: method === 'transfer' ? Number(deposit || 0) : 0 })}
        >
          Ödə
        </button>
      </div>
    </section>
  );
}
